import {
  existsSync,
  lstatSync,
  readFileSync,
} from "node:fs";
import { join } from "node:path";

export interface OpenCodePackageCacheOptions {
  packageName: string;
  packageEntry: string | null;
  packageSpecCacheRoot?: string;
  legacyPackagePaths: readonly string[];
}

export interface OpenCodePackageCacheResolution {
  packagePath: string | null;
  problem: string | null;
}

interface PackageManifestInspection {
  exists: boolean;
  problem: string | null;
}

const EXACT_SEMVER = /^\d+\.\d+\.\d+(?:-[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?(?:\+[0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*)?$/;
const SAFE_PACKAGE_SPEC = /^[0-9A-Za-z][0-9A-Za-z._+-]*$/;

function uniquePaths(paths: readonly string[]): string[] {
  return [...new Set(paths.filter((candidate) => candidate.trim().length > 0))];
}

function expectedVersion(packageName: string, packageEntry: string): string | null {
  const suffix = packageEntry.slice(`${packageName}@`.length);
  return EXACT_SEMVER.test(suffix) ? suffix : null;
}

function inspectManifest(
  packagePath: string,
  packageName: string,
  requiredVersion: string | null,
): PackageManifestInspection {
  if (!existsSync(packagePath)) return { exists: false, problem: null };

  const file = lstatSync(packagePath);
  if (file.isSymbolicLink() || !file.isFile()) {
    return {
      exists: true,
      problem: `OpenCode cache manifest ${packagePath} is not a regular file.`,
    };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(readFileSync(packagePath, "utf8"));
  } catch {
    return {
      exists: true,
      problem: `OpenCode cache manifest ${packagePath} is not valid JSON.`,
    };
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return {
      exists: true,
      problem: `OpenCode cache manifest ${packagePath} is not a JSON object.`,
    };
  }

  const manifest = parsed as Record<string, unknown>;
  if (manifest.name !== packageName) {
    return {
      exists: true,
      problem: `OpenCode cache manifest ${packagePath} does not identify ${packageName}.`,
    };
  }
  if (typeof manifest.version !== "string" || !EXACT_SEMVER.test(manifest.version)) {
    return {
      exists: true,
      problem: `OpenCode cache manifest ${packagePath} does not contain an exact semantic version.`,
    };
  }
  if (requiredVersion && manifest.version !== requiredVersion) {
    return {
      exists: true,
      problem: `OpenCode cache manifest ${packagePath} reports ${manifest.version}, but ${requiredVersion} is configured.`,
    };
  }

  return { exists: true, problem: null };
}

function packageSpecManifestPath(
  packageSpecCacheRoot: string,
  packageName: string,
  packageEntry: string,
): OpenCodePackageCacheResolution {
  if (!packageEntry.startsWith(`${packageName}@`)) {
    return { packagePath: null, problem: null };
  }
  const spec = packageEntry.slice(`${packageName}@`.length);
  if (!SAFE_PACKAGE_SPEC.test(spec)) {
    return {
      packagePath: null,
      problem: `OpenCode package spec ${packageEntry} cannot be mapped safely to its package cache.`,
    };
  }
  return {
    packagePath: join(
      packageSpecCacheRoot,
      packageEntry,
      "node_modules",
      ...packageName.split("/"),
      "package.json",
    ),
    problem: null,
  };
}

function resolveLegacyCache(
  paths: readonly string[],
  packageName: string,
  requiredVersion: string | null,
): OpenCodePackageCacheResolution {
  const valid: string[] = [];
  for (const packagePath of uniquePaths(paths)) {
    const inspection = inspectManifest(packagePath, packageName, requiredVersion);
    if (inspection.problem) {
      return { packagePath: null, problem: inspection.problem };
    }
    if (inspection.exists) valid.push(packagePath);
  }
  if (valid.length > 1) {
    return {
      packagePath: null,
      problem: `Multiple legacy OrgX OpenCode package caches are present (${valid.join(", ")}); remove the ambiguity before continuing.`,
    };
  }
  return { packagePath: valid[0] ?? null, problem: null };
}

export function resolveOpenCodePackageCache(
  options: OpenCodePackageCacheOptions,
): OpenCodePackageCacheResolution {
  const requiredVersion = options.packageEntry
    ? expectedVersion(options.packageName, options.packageEntry)
    : null;

  if (options.packageEntry && options.packageSpecCacheRoot) {
    const spec = packageSpecManifestPath(
      options.packageSpecCacheRoot,
      options.packageName,
      options.packageEntry,
    );
    if (spec.problem) return spec;
    if (spec.packagePath) {
      const inspection = inspectManifest(
        spec.packagePath,
        options.packageName,
        requiredVersion,
      );
      if (inspection.problem) {
        return { packagePath: null, problem: inspection.problem };
      }
      if (inspection.exists) {
        return { packagePath: spec.packagePath, problem: null };
      }
      if (existsSync(options.packageSpecCacheRoot)) {
        return { packagePath: null, problem: null };
      }
    }
  }

  return resolveLegacyCache(
    options.legacyPackagePaths,
    options.packageName,
    requiredVersion,
  );
}
